import { createReducer, on } from "@ngrx/store";
import * as ChildDeactivationActions from './child-deactivation.actions';
import { ChildDeactivationState, initialChildDeactivationState } from "./child-deactivation.state";
import { ChildDeactivationRequest } from "@core/models/domain/child-deactivation.domain";

// How long an optimistically-resolved request is shielded from being dropped by a history
// reload. The backend read model (CQRS projection) can lag behind an accepted command, and the
// reload fired right after a decision is exactly the one most likely to race it.
export const RECENT_DECISION_PROTECTION_MS = 10000;

/** The request as it will read once decided, built from what the decision itself already tells us. */
function toResolved(
  request: ChildDeactivationRequest,
  decision: Partial<ChildDeactivationRequest>
): ChildDeactivationRequest {
  return {
    ...request,
    ...decision,
    decisionDatetime: new Date().toISOString(),
  };
}

/**
 * Moves a request out of the pending list and into resolved, optimistically: the decision fields
 * (decidedByName, precise decisionDatetime) are filled in for real once the next history reload
 * catches up, but the pupil doesn't have to sit in "pending" until the admin refreshes the page.
 */
function decide(
  state: ChildDeactivationState,
  requestId: string,
  decision: Partial<ChildDeactivationRequest>
): ChildDeactivationState {
  const request = state.requests.find((r) => r.id === requestId);
  const resolved = request ? toResolved(request, decision) : undefined;
  return {
    ...state,
    actionInProgressId: null,
    requests: state.requests.filter((r) => r.id !== requestId),
    resolvedRequests: resolved ? [resolved, ...state.resolvedRequests] : state.resolvedRequests,
    recentLocalDecisions: resolved
      ? { ...state.recentLocalDecisions, [resolved.id]: Date.now() }
      : state.recentLocalDecisions,
  };
}

export const childDeactivationReducer = createReducer(
  initialChildDeactivationState,

  // LOAD PENDING
  on(ChildDeactivationActions.loadRequests, (state) => ({
    ...state,
    loading: true,
    error: null,
  })),
  on(ChildDeactivationActions.loadRequestsSuccess, (state, { requests }) => ({
    ...state,
    loading: false,
    requests,
  })),
  on(ChildDeactivationActions.loadRequestsFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error,
  })),

  // LOAD HISTORY: a failure here never blocks the pending list, so it sets no error banner.
  on(ChildDeactivationActions.loadResolvedRequests, (state) => ({
    ...state,
    loadingResolved: true,
  })),
  on(ChildDeactivationActions.loadResolvedRequestsSuccess, (state, { requests }) => {
    // An optimistic entry from a decision made moments ago survives a reload that came back
    // without it — the read model hasn't caught up yet, not the decision being undone.
    const now = Date.now();
    const incomingIds = new Set(requests.map((r) => r.id));
    const stillProtected = state.resolvedRequests.filter((r) => {
      const decidedAt = state.recentLocalDecisions[r.id];
      return decidedAt && now - decidedAt < RECENT_DECISION_PROTECTION_MS && !incomingIds.has(r.id);
    });
    const recentLocalDecisions: Record<string, number> = {};
    stillProtected.forEach((r) => {
      recentLocalDecisions[r.id] = state.recentLocalDecisions[r.id];
    });

    return {
      ...state,
      loadingResolved: false,
      resolvedRequests: [...stillProtected, ...requests],
      recentLocalDecisions,
    };
  }),
  on(ChildDeactivationActions.loadResolvedRequestsFailure, (state) => ({
    ...state,
    loadingResolved: false,
  })),

  // DECISIONS: the decided request leaves the pending list on success.
  on(ChildDeactivationActions.approveRequest, (state, { requestId }) => ({
    ...state,
    actionInProgressId: requestId,
    error: null,
  })),
  on(
    ChildDeactivationActions.declineRequest,
    ChildDeactivationActions.exemptRequest,
    (state, { request }) => ({
      ...state,
      actionInProgressId: request.requestId,
      error: null,
    })
  ),
  on(ChildDeactivationActions.approveRequestSuccess, (state, { requestId }) =>
    decide(state, requestId, { status: 'APPROVED' })
  ),
  on(ChildDeactivationActions.declineRequestSuccess, (state, { requestId, reason }) =>
    decide(state, requestId, { status: 'DECLINED', declineReason: reason })
  ),
  on(ChildDeactivationActions.exemptRequestSuccess, (state, { requestId, exemptionReason, exemptionNote }) =>
    decide(state, requestId, { status: 'EXEMPTED', exemptionReason, exemptionNote })
  ),
  // Action failures are shown in a snackbar by the page, so they do not set the persistent error banner.
  on(
    ChildDeactivationActions.approveRequestFailure,
    ChildDeactivationActions.declineRequestFailure,
    ChildDeactivationActions.exemptRequestFailure,
    (state) => ({
      ...state,
      actionInProgressId: null,
    })
  )
);
